import React, { useState, useEffect } from 'react';
import api from '../services/api';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('picqer');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);

  const [picqerSettings, setPicqerSettings] = useState({
    apiKey: '',
    subdomain: '',
    enabled: true,
    syncInterval: 5,
    fullSyncEnabled: false,
    fullSyncDay: 'daily',
    fullSyncHour: 6
  });

  const [connectionStatus, setConnectionStatus] = useState(null);

  useEffect(() => {
    loadPicqerSettings();
  }, []);

  const loadPicqerSettings = async () => {
    try {
      const response = await api.get('/settings/picqer');
      const data = response.data;
      setPicqerSettings({
        subdomain: data.subdomain || '',
        apiKey: '', // Don't load API key for security
        enabled: data.enabled !== false,
        syncInterval: data.syncInterval || 5,
        fullSyncEnabled: data.fullSyncEnabled || false,
        fullSyncDay: data.fullSyncDay || 'daily',
        fullSyncHour: data.fullSyncHour !== undefined ? data.fullSyncHour : 6
      });
      if (data.configured) {
        setConnectionStatus({
          success: true,
          message: `Geconfigureerd: ${data.subdomain}.picqer.com`
        });
      }
    } catch (error) {
      console.error('Failed to load Picqer settings:', error);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    setConnectionStatus(null);
    try {
      const response = await api.post('/settings/picqer/test');
      setConnectionStatus({
        success: response.data.success,
        message: response.data.success
          ? `Verbinding succesvol! (${response.data.subdomain}.picqer.com)`
          : response.data.error || 'Verbinding mislukt'
      });
    } catch (error) {
      setConnectionStatus({
        success: false,
        message: error.response?.data?.error || 'Verbinding mislukt'
      });
    } finally {
      setLoading(false);
    }
  };

  const syncOrders = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const response = await api.post('/picqer/sync/orders');
      setMessage({
        type: 'success',
        text: response.data.message || 'Orders gesynchroniseerd'
      });
    } catch (error) {
      setMessage({
        type: 'danger',
        text: error.response?.data?.error || 'Sync mislukt'
      });
    } finally {
      setSyncing(false);
    }
  };

  const syncProducts = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const response = await api.post('/picqer/sync/products');
      setMessage({
        type: 'success',
        text: response.data.message || 'Producten gesynchroniseerd'
      });
    } catch (error) {
      setMessage({
        type: 'danger',
        text: error.response?.data?.error || 'Sync mislukt'
      });
    } finally {
      setSyncing(false);
    }
  };

  const syncAll = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const response = await api.post('/picqer/sync/all');
      setMessage({
        type: 'success',
        text: response.data.message || 'Volledige synchronisatie voltooid'
      });
    } catch (error) {
      setMessage({
        type: 'danger',
        text: error.response?.data?.error || 'Sync mislukt'
      });
    } finally {
      setSyncing(false);
    }
  };

  const saveSettings = async () => {
    // Validate
    if (!picqerSettings.subdomain) {
      setMessage({ type: 'danger', text: 'Subdomain is verplicht' });
      return;
    }
    if (!picqerSettings.apiKey) {
      setMessage({ type: 'danger', text: 'API key is verplicht' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      await api.put('/settings/picqer', {
        subdomain: picqerSettings.subdomain,
        apiKey: picqerSettings.apiKey,
        enabled: picqerSettings.enabled,
        syncInterval: picqerSettings.syncInterval,
        fullSyncEnabled: picqerSettings.fullSyncEnabled,
        fullSyncDay: picqerSettings.fullSyncDay,
        fullSyncHour: picqerSettings.fullSyncHour
      });

      setMessage({ type: 'success', text: 'Instellingen opgeslagen!' });

      // Clear API key from form (for security)
      setPicqerSettings(prev => ({ ...prev, apiKey: '' }));

      // Test connection automatically after saving
      await testConnection();
    } catch (error) {
      setMessage({
        type: 'danger',
        text: error.response?.data?.error || 'Opslaan mislukt'
      });
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'picqer', label: 'Picqer API', icon: 'bi-cloud' },
    { id: 'general', label: 'Algemeen', icon: 'bi-gear' },
    { id: 'users', label: 'Gebruikers', icon: 'bi-people' }
  ];

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Instellingen</h1>
        <p className="text-muted">Beheer systeem configuratie en integraties</p>
      </div>

      {message && (
        <div className={`alert alert-${message.type} alert-dismissible`}>
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage(null)}
          ></button>
        </div>
      )}

      <div className="row">
        <div className="col-md-3">
          <div className="card">
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`list-group-item list-group-item-action d-flex align-items-center ${
                      activeTab === tab.id ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <i className={`bi ${tab.icon} me-3`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-9">
          {/* Picqer Settings */}
          {activeTab === 'picqer' && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Picqer Integratie</h5>
              </div>
              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label">API Subdomain *</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={picqerSettings.subdomain}
                        onChange={(e) => setPicqerSettings({ ...picqerSettings, subdomain: e.target.value })}
                        placeholder="jouwbedrijf"
                      />
                      <span className="input-group-text">.picqer.com</span>
                    </div>
                    <small className="text-muted">
                      Alleen het subdomain, bijv. "tafelzeilonline"
                    </small>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">API Key *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={picqerSettings.apiKey}
                      onChange={(e) => setPicqerSettings({ ...picqerSettings, apiKey: e.target.value })}
                      placeholder="Voer API key in"
                    />
                    <small className="text-muted">
                      Te vinden in Picqer onder Instellingen &gt; API
                    </small>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="form-check form-switch mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="syncEnabled"
                        checked={picqerSettings.enabled}
                        onChange={(e) => setPicqerSettings({ ...picqerSettings, enabled: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="syncEnabled">
                        Integratie actief
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Sync interval (minuten)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={picqerSettings.syncInterval}
                      onChange={(e) => setPicqerSettings({ ...picqerSettings, syncInterval: parseInt(e.target.value) || 5 })}
                      min="1"
                      max="60"
                    />
                  </div>
                </div>

                <hr className="my-4" />

                <h6 className="mb-3">Volledige synchronisatie</h6>
                <p className="text-muted small mb-3">
                  Plan een dagelijkse volledige sync om alle orders te synchroniseren (niet alleen nieuwe).
                </p>

                <div className="row mb-4">
                  <div className="col-md-4">
                    <div className="form-check form-switch mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="fullSyncEnabled"
                        checked={picqerSettings.fullSyncEnabled}
                        onChange={(e) => setPicqerSettings({ ...picqerSettings, fullSyncEnabled: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="fullSyncEnabled">
                        Volledige sync actief
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Dag</label>
                    <select
                      className="form-select"
                      value={picqerSettings.fullSyncDay}
                      onChange={(e) => setPicqerSettings({ ...picqerSettings, fullSyncDay: e.target.value })}
                      disabled={!picqerSettings.fullSyncEnabled}
                    >
                      <option value="daily">Dagelijks</option>
                      <option value="monday">Maandag</option>
                      <option value="tuesday">Dinsdag</option>
                      <option value="wednesday">Woensdag</option>
                      <option value="thursday">Donderdag</option>
                      <option value="friday">Vrijdag</option>
                      <option value="saturday">Zaterdag</option>
                      <option value="sunday">Zondag</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Tijdstip</label>
                    <select
                      className="form-select"
                      value={picqerSettings.fullSyncHour}
                      onChange={(e) => setPicqerSettings({ ...picqerSettings, fullSyncHour: parseInt(e.target.value) })}
                      disabled={!picqerSettings.fullSyncEnabled}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {i.toString().padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="d-flex gap-2 mb-4">
                  <button
                    className="btn btn-primary"
                    onClick={saveSettings}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Opslaan...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        Instellingen opslaan
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    onClick={testConnection}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Testen...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-plug me-2"></i>
                        Test verbinding
                      </>
                    )}
                  </button>
                </div>

                {connectionStatus && (
                  <div className={`alert alert-${connectionStatus.success ? 'success' : 'danger'}`}>
                    <i className={`bi ${connectionStatus.success ? 'bi-check-circle' : 'bi-x-circle'} me-2`}></i>
                    {connectionStatus.message}
                  </div>
                )}

                <hr />

                <h6 className="mb-3">Handmatige synchronisatie</h6>
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <i className="bi bi-inbox fs-2 text-primary mb-2"></i>
                        <h6>Orders</h6>
                        <p className="text-muted small mb-2">
                          Importeer nieuwe orders uit Picqer
                        </p>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={syncOrders}
                          disabled={syncing}
                        >
                          {syncing ? 'Syncing...' : 'Sync orders'}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <i className="bi bi-box fs-2 text-success mb-2"></i>
                        <h6>Producten</h6>
                        <p className="text-muted small mb-2">
                          Update productgegevens uit Picqer
                        </p>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={syncProducts}
                          disabled={syncing}
                        >
                          {syncing ? 'Syncing...' : 'Sync producten'}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <i className="bi bi-arrow-repeat fs-2 text-warning mb-2"></i>
                        <h6>Volledige sync</h6>
                        <p className="text-muted small mb-2">
                          Synchroniseer alles
                        </p>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={syncAll}
                          disabled={syncing}
                        >
                          {syncing ? 'Syncing...' : 'Sync alles'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Algemene Instellingen</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Bedrijfsnaam</label>
                  <input
                    type="text"
                    className="form-control"
                    defaultValue="Tafelzeil Online"
                  />
                </div>
                                <div className="mb-3">
                  <label className="form-label">Tijdzone</label>
                  <select className="form-select" defaultValue="Europe/Amsterdam">
                    <option value="Europe/Amsterdam">Europe/Amsterdam</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
                <button className="btn btn-primary">
                  <i className="bi bi-save me-2"></i>
                  Opslaan
                </button>
              </div>
            </div>
          )}

          {/* Users Settings */}
          {activeTab === 'users' && (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Gebruikersbeheer</h5>
                <button className="btn btn-primary btn-sm">
                  <i className="bi bi-plus-lg me-2"></i>
                  Nieuwe gebruiker
                </button>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Naam</th>
                        <th>Email</th>
                        <th>Rol</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Admin</td>
                        <td>admin@tafelzeilonline.nl</td>
                        <td><span className="badge bg-primary">Admin</span></td>
                        <td><span className="badge bg-success">Actief</span></td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary">
                            <i className="bi bi-pencil"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
